import { useRef, useState } from "react";
import { Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { ApercuDialog } from "@/components/ApercuDialog";
import { aControler, ImportControleDialog } from "@/components/ImportControleDialog";
import { estImage, ImageImportDialog } from "@/components/ImageImportDialog";

export const guessLogement = (name) => {
  const n = name.toUpperCase().replace(/[^A-Z0-9]+/g, " ");
  const ouest = /\bOUEST\b/.test(n), est = /\bEST\b/.test(n);
  if (ouest && est) return "";
  if (ouest) return "Appartement 1 OUEST";
  if (est) return "Appartement 2 EST";
  return "";
};

export const postWithRetry = async (fd) => {
  try {
    return await api.post("/imports", fd);
  } catch (e) {
    if (e.response?.status === 404 && !e.response?.data?.detail) {
      await new Promise((r) => setTimeout(r, 2500));
      return api.post("/imports", fd);
    }
    throw e;
  }
};

const FileRow = ({ item, idx, withLogement, onChange, onRemove, onCheck, type }) => (
  <li className="flex flex-col gap-1.5 rounded-md border border-slate-200 p-2" data-testid={`upload-file-${type}-${idx}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-xs font-medium">{item.file.name}</span>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => onCheck(item)} className={`text-xs text-emerald-700 hover:underline ${estImage(item.file) ? "hidden" : ""}`} data-testid={`upload-check-${type}-${idx}`}>Vérifier les colonnes</button>
        <button onClick={() => onRemove(idx)} className="text-slate-400 hover:text-red-600" data-testid={`upload-remove-${type}-${idx}`}><X className="h-3.5 w-3.5" /></button>
      </div>
    </div>
    {withLogement && (
      <Input className="h-8 text-xs" placeholder="Logement (vide = nom indiqué dans le fichier)" value={item.logement}
        onChange={(e) => onChange(idx, e.target.value)} data-testid={`upload-logement-${type}-${idx}`} />
    )}
  </li>
);

export const MultiUploadCard = ({ source, onDone }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [check, setCheck] = useState(null);
  const [controles, setControles] = useState([]);
  const [images, setImages] = useState([]);
  const inputRef = useRef(null);
  const withLogement = source.type !== "banque";

  const addFiles = (list) => {
    setItems((cur) => [...cur, ...Array.from(list).map((file) => ({ file, logement: guessLogement(file.name) }))]);
    inputRef.current.value = "";
  };

  const submit = async () => {
    setLoading(true);
    let inserted = 0, doublons = 0, affectees = 0, interets = 0;
    const failed = [], resultats = [], ids = [];
    const fichiers = items.filter((it) => !estImage(it.file));
    for (const it of fichiers) {
      const fd = new FormData();
      fd.append("type", source.type);
      fd.append("logement", it.logement);
      fd.append("file", it.file);
      try {
        const { data } = await postWithRetry(fd);
        inserted += data.inserted;
        doublons += data.doublons;
        affectees += data.affectees || 0;
        interets += data.interets_ajoutes || 0;
        if (source.type !== "banque" && data.import_id) ids.push(data.import_id);
        if (data.logements_inconnus?.length) {
          toast.warning(`${it.file.name} : nom(s) d'annonce inconnu(s) — ${data.logements_inconnus.map((x) => `« ${x.nom} » (${x.n})`).join(", ")}. Ajoutez-les dans Paramètres › Logements › « Noms d'annonce Airbnb / Booking ».`, { duration: Infinity, closeButton: true });
        }
        if (aControler(data)) resultats.push({ fichier: it.file.name, ...data });
      } catch (e) {
        const msg = e.response?.status === 404 && !e.response?.data?.detail ? "serveur momentanément indisponible, réessayez" : errMsg(e);
        failed.push(it);
        toast.error(`${it.file.name} : ${msg}`);
      }
    }
    if (fichiers.length > failed.length) toast.success(`${inserted} ligne(s) importée(s)${doublons ? ` · ${doublons} doublon(s) ignoré(s)` : ""}${affectees ? ` · ${affectees} charge(s) affectée(s) par vos règles` : ""}${interets ? ` · ${interets} remboursement(s) de prêt (intérêts hypothécaires)` : ""}`);
    setItems(failed);
    setControles(resultats);
    setLoading(false);
    setImages(items.filter((it) => estImage(it.file)));
    if (fichiers.length) onDone(ids);
  };

  const imageFinie = (it, data) => {
    if (data) {
      toast.success(`${it.file.name} : ${data.inserted} ligne(s) importée(s)${data.doublons ? ` · ${data.doublons} doublon(s) ignoré(s)` : ""}`);
      onDone(source.type !== "banque" && data.import_id ? [data.import_id] : []);
    } else {
      setItems((cur) => [...cur, it]);
    }
    setImages((cur) => cur.slice(1));
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col rise" data-testid={`upload-card-${source.type}`}>
      <source.icon className={`h-5 w-5 ${source.accent}`} />
      <h3 className="mt-3 font-semibold">{source.title}</h3>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">{source.hint}</p>
      <label className="mt-4 flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed border-slate-300 px-3 py-5 text-center transition-colors hover:border-emerald-600 hover:bg-emerald-50/40">
        <UploadCloud className="h-5 w-5 text-slate-400" />
        <span className="text-xs text-slate-600" data-testid={`upload-filename-${source.type}`}>
          {items.length ? `${items.length} fichier(s) sélectionné(s) · ajouter` : "Un ou plusieurs fichiers CSV, Excel, PDF ou images (captures, photos)"}
        </span>
        <input ref={inputRef} type="file" multiple accept=".csv,.xlsx,.xls,.pdf,.txt,text/csv,text/plain,text/comma-separated-values,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => addFiles(e.target.files)} data-testid={`upload-input-${source.type}`} />
      </label>
      {items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {items.map((it, i) => (
            <FileRow key={`${it.file.name}-${i}`} item={it} idx={i} type={source.type} withLogement={withLogement}
              onChange={(idx, v) => setItems(items.map((x, j) => (j === idx ? { ...x, logement: v } : x)))}
              onRemove={(idx) => setItems(items.filter((_, j) => j !== idx))}
              onCheck={(it) => setCheck({ file: it.file, type: source.type })} />
          ))}
        </ul>
      )}
      <Button className="mt-3 bg-emerald-700 hover:bg-emerald-800" disabled={!items.length || loading} onClick={submit} data-testid={`upload-submit-${source.type}`}>
        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Importer {items.length > 1 ? `les ${items.length} fichiers` : ""}
      </Button>
      <ApercuDialog target={check} onClose={() => setCheck(null)} />
      <ImportControleDialog resultats={controles} onClose={() => setControles([])} onChange={onDone} />
      <ImageImportDialog type={source.type} images={images} onFini={imageFinie} />
    </div>
  );
};
